import {
  BlockTitle,
  Button,
  FileButton,
  PhotoGrid,
  PhotoTile,
  StageItem,
  StageList,
  toast,
} from '@westhetics/ui'
import { useState } from 'react'
import { errorMessage, fieldErrors } from '@/lib/errors'
import {
  useArchiveStagePhoto,
  useRestoreStagePhoto,
  useUploadStagePhoto,
  type Stage,
  type TreatmentPlan,
} from './api'
import {
  IMAGE_CONSENT_LABELS,
  PHOTOS_FOOTER,
  STAGES,
  STAGE_LABELS,
  photoCaption,
  photoCount,
  recordedStagesText,
} from './labels'
import { PHOTO_ACCEPT, type PreparedPhoto } from './photo-file'
import { usePhotoCapture } from './use-photo-capture'

/**
 * A aba Fotos (RN-PLA-15 a 19): quatro etapas em linha com contagem, "+ Adicionar" (galeria),
 * "Tirar foto" (câmera) e "Abrir QR" por etapa; as miniaturas abaixo, com "arquivar" e Desfazer.
 * Até 12 fotos por etapa; o consentimento de imagem não bloqueia a foto, só a saída (P-PLA-07).
 */

export const STAGE_LIMIT = 12

interface LocalUpload {
  key: string
  stage: Stage
  photo: PreparedPhoto
  source: 'desktop' | 'gallery'
  state: 'uploading' | 'failed'
  error?: string
}

export function PhotosTab({
  plan,
  readOnly,
  onOpenQr,
}: {
  plan: TreatmentPlan
  readOnly: boolean
  onOpenQr: (stage: Stage) => void
}) {
  const upload = useUploadStagePhoto(plan.id)
  const archive = useArchiveStagePhoto(plan.id)
  const restore = useRestoreStagePhoto(plan.id)
  const capture = usePhotoCapture()
  const [uploads, setUploads] = useState<LocalUpload[]>([])

  async function send(item: LocalUpload) {
    try {
      await upload.mutateAsync({ stage: item.stage, photo: item.photo, source: item.source })
      setUploads((current) => current.filter((u) => u.key !== item.key))
    } catch (error) {
      const fields = fieldErrors(error)
      const message = fields.file ?? fields.sizeBytes ?? fields.mimeType ?? errorMessage(error)
      setUploads((current) =>
        current.map((u) => (u.key === item.key ? { ...u, state: 'failed', error: message } : u)),
      )
    }
  }

  async function add(stage: Stage, files: File[], source: 'desktop' | 'gallery') {
    const room =
      STAGE_LIMIT -
      (plan.photos[stage]?.length ?? 0) -
      uploads.filter((u) => u.stage === stage).length
    const accepted = files.slice(0, Math.max(0, room))
    if (accepted.length < files.length) {
      toast(`Esta etapa já tem ${STAGE_LIMIT} fotos, o máximo. Arquive uma para adicionar outra.`)
    }
    await capture(
      accepted,
      async (photo) => {
        const item: LocalUpload = {
          key: `${stage}-${Date.now()}-${Math.random()}`,
          stage,
          photo,
          source,
          state: 'uploading',
        }
        setUploads((current) => [...current, item])
        await send(item)
      },
      (message) => toast(message),
    )
  }

  function archivePhoto(stage: Stage, photoId: string) {
    archive.mutate(
      { stage, photoId },
      {
        onSuccess: () =>
          toast('Foto arquivada', {
            undo: () => restore.mutate(photoId, { onError: (error) => toast(errorMessage(error)) }),
          }),
        onError: (error) => toast(errorMessage(error)),
      },
    )
  }

  const consent = plan.imageConsent !== 'yes' ? IMAGE_CONSENT_LABELS[plan.imageConsent] : null

  return (
    <div className="flex flex-col gap-4">
      <BlockTitle
        title="Fotos do tratamento"
        note={
          <>
            {recordedStagesText(plan.recordedStageCount)}
            {consent ? (
              <>
                {' · uso de imagem: '}
                <span className="text-ink">{consent}</span>
                {' — as fotos ficam restritas ao prontuário'}
              </>
            ) : null}
          </>
        }
      />
      <StageList>
        {STAGES.map((stage) => {
          const photos = plan.photos[stage] ?? []
          const pending = uploads.filter((u) => u.stage === stage)
          const full = photos.length + pending.length >= STAGE_LIMIT
          const label = STAGE_LABELS[stage]
          return (
            <StageItem
              key={stage}
              title={label.title}
              description={label.description}
              count={photoCount(photos.length)}
              countPending={photos.length === 0 && plan.status === 'open'}
              actions={
                readOnly ? undefined : (
                  <>
                    <FileButton
                      variant="text"
                      size="sm"
                      accept={PHOTO_ACCEPT}
                      capture="environment"
                      disabled={full}
                      onFiles={(files) => void add(stage, files, 'desktop')}
                    >
                      {full ? `Etapa cheia (${STAGE_LIMIT})` : 'Tirar foto'}
                    </FileButton>
                    {!full ? (
                      <FileButton
                        variant="text"
                        size="sm"
                        accept={PHOTO_ACCEPT}
                        multiple
                        onFiles={(files) => void add(stage, files, 'gallery')}
                      >
                        + Adicionar
                      </FileButton>
                    ) : null}
                    <Button variant="text" size="sm" onClick={() => onOpenQr(stage)}>
                      Abrir QR
                    </Button>
                  </>
                )
              }
            >
              {photos.length > 0 || pending.length > 0 ? (
                <PhotoGrid>
                  {photos.map((photo) => (
                    <PhotoTile
                      key={photo.id}
                      src={photo.url}
                      href={photo.url ?? undefined}
                      alt={`Foto ${photo.position ?? ''} da etapa ${label.title}`.replace(
                        '  ',
                        ' ',
                      )}
                      caption={photoCaption(photo)}
                      action={
                        readOnly ? undefined : (
                          <button
                            type="button"
                            className="hover:text-ink cursor-pointer"
                            onClick={() => archivePhoto(stage, photo.id)}
                          >
                            arquivar
                          </button>
                        )
                      }
                    />
                  ))}
                  {pending.map((item) => (
                    <PhotoTile
                      key={item.key}
                      src={item.photo.thumbnailDataUrl}
                      alt={
                        item.state === 'failed'
                          ? `Foto não enviada da etapa ${label.title}`
                          : `Foto enviando para ${label.title}`
                      }
                      state={item.state}
                      placeholder={item.state === 'failed' ? 'Não enviada' : 'Enviando…'}
                      caption={item.state === 'failed' ? item.error : 'Enviando…'}
                      action={
                        item.state === 'failed' ? (
                          <span className="flex gap-2">
                            <button
                              type="button"
                              className="text-plum cursor-pointer"
                              onClick={() =>
                                void send({ ...item, state: 'uploading', error: undefined })
                              }
                            >
                              enviar de novo
                            </button>
                            <button
                              type="button"
                              className="hover:text-ink cursor-pointer"
                              onClick={() => setUploads((c) => c.filter((u) => u.key !== item.key))}
                            >
                              descartar
                            </button>
                          </span>
                        ) : undefined
                      }
                    />
                  ))}
                </PhotoGrid>
              ) : null}
            </StageItem>
          )
        })}
      </StageList>
      <p className="font-body text-secondary text-[12px] leading-[1.6] font-light">
        {PHOTOS_FOOTER}
      </p>
    </div>
  )
}
